import axios from 'axios';
import { Configuration } from '@src/generated';
import { getEnvironment, isMyAtupriOrAkupoPath } from './env';
import { log } from '@src/middleware';

const ATUPRI_DOMAIN = 'atupri.ch';
const ATUPRINET_DOMAIN = 'atuprinet.ch';
const OPENSHIFT_PREFIX = 'ocp';

const getOrigin = () => {
    return window.location ? window.location.hostname : ATUPRI_DOMAIN;
};

const isInternal = () => {
    return (
        getOrigin().includes(OPENSHIFT_PREFIX) || getOrigin().includes(ATUPRINET_DOMAIN) || getEnvironment() === 'local'
    );
};

const getInternalUrl = () => {
    const environment = getEnvironment();
    const shortEnvironment = getShortEnvironment(environment);
    switch (environment) {
        case 'local':
            return formatOpenshiftUrl('d', '-d');
        case 'prod':
            return formatOpenshiftUrl(shortEnvironment, '');
        default:
            return formatOpenshiftUrl(shortEnvironment, `-${shortEnvironment}`);
    }
};

const getAccessToken = () => {
    let token;
    let tokenType;

    for (let key in sessionStorage) {
        if (key.includes('oidc.user') && sessionStorage.getItem(key)) {
            const parsedData = JSON.parse(sessionStorage.getItem(key));

            token = parsedData.access_token;
            tokenType = parsedData.token_type;
            break;
        }
    }

    return token && tokenType ? `${tokenType} ${token}` : undefined;
};

const getPublicUrl = () => {
    const environment = getEnvironment();
    return formatPublicUrl(environment === 'prod' ? '' : getShortEnvironment(environment));
};

const getShortEnvironment = environment => {
    return environment?.charAt(0) ?? '';
};

const formatOpenshiftUrl = (prefix, subDomain) => {
    return `https://${prefix}myatupri-api.ocp${subDomain}.${ATUPRI_DOMAIN}`;
};

const formatPublicUrl = prefix => {
    return `https://${prefix}myatupri-api.${ATUPRI_DOMAIN}`;
};

export const getApiBasePath = () => {
    return isInternal() ? getInternalUrl() : getPublicUrl();
};

export const getLoggedInUserId = (submission = undefined) => {
    let userId = NaN;

    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development' || process.env.JEST) {
        userId = parseInt(process.env.REACT_APP_USER_ID ?? '');
    } else if (!!window.__ENV__ && !!window.__ENV__.USER_ID) {
        userId = parseInt(window.__ENV__.USER_ID);
    } else if (submission) {
        userId = submission.attributes.loggedInUserInsuredNr;
    }

    if (isNaN(userId)) {
        userId = null;

        if (isMyAtupriOrAkupoPath) {
            log.debug('=== No logged in user ===');
        }
    }

    return userId;
};

// The base configuration for all services
const myAtupriConfig = new Configuration({
    basePath: getApiBasePath(),
    baseOptions: {
        withCredentials: 'include',
        headers: {
            accept: 'application/vnd.myatupri.api.v3+json',
        },
    },
});

export default myAtupriConfig;

const onFullFilledRequest = config => {
    const authorizationHeader = getAccessToken();

    if (authorizationHeader) {
        config.headers = { ...config.headers, Authorization: authorizationHeader };
    }
    return config;
};

export const onRejectedRequest = error => {
    return Promise.reject(new Error(error));
};

// API response handling - https://github.com/axios/axios#interceptors
axios.interceptors.response.use(
    function (response) {
        // Any status code that lies within the range of 2xx triggers this function

        return response;
    },
    function (error) {
        // If there is no response, the API call was cancelled in frontend, that shouldn't trigger a reject or redirect
        if (!error?.response) {
            return;
        }
        // Any status code that falls outside the range of 2xx triggers this function
        // If the API returns a 401 the session is no longer valid. The logout is forced
        else if (error?.response?.status === 401) {
            window.location.href = '/login/sls/auth?cmd=logoutmain';
        }
        return Promise.reject(new Error(error));
    }
);

axios.interceptors.request.use(onFullFilledRequest, onRejectedRequest);
